import Service, { service } from '@ember/service';
import type FeaturesService from './features.ts';

interface TelemetryUser {
  id: string;
  name?: string;
  role?: string;
  relationshipType?: string;
}

interface TelemetryContext {
  company_id?: string;
  meta?: Record<string, string>;
}

interface TelemetryConfig {
  endpoint: string;
  app: string;
  token?: string;
  enabled?: boolean;
  /** Flush interval in ms (default: 10000) */
  flushInterval?: number;
  /** Max events per flush (default: 25) */
  batchSize?: number;
  /** Endpoint for session replay data (separate from RUM events) */
  replayEndpoint?: string;
  /** Enable rrweb session recording (default: false) */
  recordSession?: boolean;
  /** Replay chunk flush interval in ms (default: 15000) */
  replayFlushInterval?: number;
}

interface TelemetryEvent {
  event_type: 'page_view' | 'error' | 'request' | 'action' | 'interaction';
  app: string;
  session_id: string;
  timestamp: string;
  url: string;
  route?: string;
  referrer?: string;
  user_agent: string;
  user?: TelemetryUser;
  context?: TelemetryContext;
  page_view?: Record<string, number>;
  error?: Record<string, unknown>;
  request?: Record<string, unknown>;
  action?: Record<string, unknown>;
  interaction?: Record<string, unknown>;
}

/**
 * Lightweight telemetry service that supplements Datadog RUM (Phase 1a parallel run).
 * Will fully replace Datadog RUM once validated.
 *
 * Key design: **passive observer** — uses standard browser APIs
 * (PerformanceObserver, addEventListener, sendBeacon) without
 * monkey-patching any prototypes. This is the fundamental difference
 * from Datadog RUM.
 *
 * Usage:
 *   // In application.ts beforeModel():
 *   this.telemetry.configure({ endpoint: '...', app: 'tio-admin' });
 *
 *   // In authenticated.ts beforeModel():
 *   this.telemetry.setUser({ id: user.id, name: user.person.fullName });
 */
export default class TelemetryService extends Service {
  @service declare features: FeaturesService;

  private config: TelemetryConfig | null = null;
  private user: TelemetryUser | null = null;
  private context: TelemetryContext | null = null;
  private sessionId = '';
  private buffer: TelemetryEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private observers: PerformanceObserver[] = [];
  private boundErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private boundUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;
  private boundVisibilityChange: (() => void) | null = null;
  private boundClickHandler: ((event: MouseEvent) => void) | null = null;
  private boundScrollHandler: (() => void) | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private maxScrollDepth = 0;
  private currentRoute = '';

  // rrweb session replay state
  private replayBuffer: unknown[] = [];
  private replayFlushTimer: ReturnType<typeof setInterval> | null = null;
  private replayStopFn: (() => void) | null = null;
  private replayChunkIndex = 0;
  private static readonly REPLAY_BUFFER_CAP = 500;

  configure = (config: TelemetryConfig) => {
    if (this.config) return; // Already configured

    this.config = {
      flushInterval: 10_000,
      batchSize: 25,
      enabled: true,
      ...config,
    };

    if (!this.config.enabled) return;

    this.sessionId = this.generateSessionId();
    this.startObservers();
    this.startFlushTimer();

    if (this.config.recordSession && this.config.replayEndpoint) {
      this.startSessionRecording();
    }
  };

  setUser = (user: TelemetryUser) => {
    this.user = user;
  };

  setContext = (context: TelemetryContext) => {
    this.context = context;
  };

  setRoute = (routeName: string) => {
    this.currentRoute = routeName;
  };

  /**
   * Track an SPA route transition as a page_view event.
   * Call from the router's `routeDidChange` handler.
   *
   * On the first call (initial page load) this only sets the route for
   * enrichment — the PerformanceObserver navigation entry already emits
   * that page_view. Subsequent calls emit an SPA transition page_view.
   */
  trackRouteChange = (routeName: string) => {
    const isInitial = !this.currentRoute;
    this.currentRoute = routeName;
    this.maxScrollDepth = 0;

    if (!isInitial) {
      this.push({
        event_type: 'page_view',
        page_view: { spa_transition: 1 },
      });
    }
  };

  trackAction = (name: string, target?: string) => {
    this.push({
      event_type: 'action',
      action: { name, target },
    });
  };

  trackError = (error: Error | string, context?: Record<string, unknown>) => {
    const message = error instanceof Error ? error.message : error;
    const stack = error instanceof Error ? error.stack : undefined;

    this.push({
      event_type: 'error',
      error: { message, stack, type: 'manual', ...context },
    });
  };

  // -- Lifecycle --

  willDestroy() {
    super.willDestroy();
    this.flush();
    this.flushReplayBuffer();
    this.stopObservers();
    this.stopSessionRecording();
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.replayFlushTimer) {
      clearInterval(this.replayFlushTimer);
      this.replayFlushTimer = null;
    }
  }

  // -- Private --

  private push(
    partial: Omit<TelemetryEvent, 'app' | 'session_id' | 'timestamp' | 'url' | 'user_agent'>
  ) {
    if (!this.config?.enabled) return;

    const event: TelemetryEvent = {
      ...partial,
      app: this.config.app,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      route: this.currentRoute || undefined,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
    };

    if (this.user) {
      event.user = this.user;
    }

    if (this.context) {
      event.context = this.context;
    }

    this.buffer.push(event);

    if (this.buffer.length >= (this.config.batchSize ?? 25)) {
      this.flush();
    }
  }

  private flush = () => {
    if (!this.config?.enabled || this.buffer.length === 0) return;

    const events = this.buffer.splice(0, this.config.batchSize ?? 25);
    const payload = JSON.stringify(events);
    const url = this.config.endpoint;

    this.sendViaFetch(url, payload);
  };

  private sendViaFetch(url: string, payload: string) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.config?.token) {
      headers['X-Telemetry-Token'] = this.config.token;
    }

    // Fire and forget — telemetry should never block the app. We use the Fetch API
    // with `keepalive: true` so the request can continue during page unload or
    // navigation, similar to sendBeacon-style behavior but with explicit control
    // over headers and credentials.
    // Note: credentials: 'omit' is required because the API uses a wildcard
    // Access-Control-Allow-Origin header, which is incompatible with
    // credentials: 'include'. The endpoint authenticates via X-Telemetry-Token,
    // not cookies.
    fetch(url, {
      method: 'POST',
      headers,
      body: payload,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Silently swallow — telemetry failures are not actionable for users
    });
  }

  private startFlushTimer() {
    this.flushTimer = setInterval(this.flush, this.config?.flushInterval ?? 10_000);

    // Also flush on page hide (tab switch, navigation, close)
    this.boundVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
  }

  private startObservers() {
    this.observeWebVitals();
    this.observeErrors();
    this.observeResources();
    this.observeInteractions();
  }

  private stopObservers() {
    for (const observer of this.observers) {
      observer.disconnect();
    }
    this.observers = [];

    if (this.boundErrorHandler) {
      window.removeEventListener('error', this.boundErrorHandler);
    }
    if (this.boundUnhandledRejection) {
      window.removeEventListener('unhandledrejection', this.boundUnhandledRejection);
    }
    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    }
    if (this.boundClickHandler) {
      document.removeEventListener('click', this.boundClickHandler, true);
    }
    if (this.boundScrollHandler) {
      window.removeEventListener('scroll', this.boundScrollHandler);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  /**
   * Core Web Vitals via PerformanceObserver.
   * Multiple observers are spec-compliant — no conflict with DD RUM or any other tool.
   */
  private observeWebVitals() {
    const vitals: Record<string, number> = {};
    let navigationSent = false;

    // LCP (Largest Contentful Paint)
    this.safeObserve('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (last) {
        vitals['lcp'] = Math.round(last.startTime);
      }
    });

    // FCP (First Contentful Paint)
    this.safeObserve('paint', (entries) => {
      for (const entry of entries) {
        if (entry.name === 'first-contentful-paint') {
          vitals['fcp'] = Math.round(entry.startTime);
        }
      }
    });

    // CLS (Cumulative Layout Shift)
    this.safeObserve('layout-shift', (entries) => {
      let clsValue = vitals['cls'] ?? 0;
      for (const entry of entries) {
        const layoutEntry = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
        if (!layoutEntry.hadRecentInput) {
          clsValue += layoutEntry.value;
        }
      }
      vitals['cls'] = Math.round(clsValue * 1000) / 1000;
    });

    // INP (Interaction to Next Paint)
    this.safeObserve('event', (entries) => {
      for (const entry of entries) {
        const eventEntry = entry as PerformanceEntry & { duration: number };
        const duration = eventEntry.duration;
        if (!vitals['inp'] || duration > vitals['inp']) {
          vitals['inp'] = duration;
        }
      }
    });

    // Navigation timing (TTFB, DOM interactive, load time)
    this.safeObserve('navigation', (entries) => {
      if (navigationSent) return;
      navigationSent = true;

      const nav = entries[0] as PerformanceNavigationTiming;
      if (nav) {
        vitals['ttfb'] = Math.round(nav.responseStart - nav.requestStart);
        vitals['dom_interactive'] = Math.round(nav.domInteractive);
        vitals['dom_complete'] = Math.round(nav.domComplete);
        vitals['load_time'] = Math.round(nav.loadEventEnd);
      }

      // Send page_view after navigation timing is available
      // Delay slightly to allow LCP/FCP to settle
      setTimeout(() => {
        this.push({
          event_type: 'page_view',
          page_view: { ...vitals },
        });
      }, 3000);
    });
  }

  /**
   * Capture unhandled JS errors and promise rejections.
   */
  private observeErrors() {
    this.boundErrorHandler = (event: ErrorEvent) => {
      this.push({
        event_type: 'error',
        error: {
          message: event.message,
          stack: event.error?.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          type: 'uncaught',
        },
      });
    };

    this.boundUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.push({
        event_type: 'error',
        error: {
          message: reason?.message || String(reason),
          stack: reason?.stack,
          type: 'unhandled_rejection',
        },
      });
    };

    window.addEventListener('error', this.boundErrorHandler);
    window.addEventListener('unhandledrejection', this.boundUnhandledRejection);
  }

  /**
   * Track slow network requests via Resource Timing API.
   * Only captures requests > 1s duration. Does not detect HTTP status failures
   * (Resource Timing API doesn't expose status codes).
   */
  private observeResources() {
    this.safeObserve('resource', (entries) => {
      for (const entry of entries) {
        const resource = entry as PerformanceResourceTiming;
        const duration = resource.duration;

        // Only track slow requests (>1s)
        if (duration > 1000) {
          this.push({
            event_type: 'request',
            request: {
              url: resource.name,
              duration: Math.round(duration),
              initiator_type: resource.initiatorType,
            },
          });
        }
      }
    });
  }

  /**
   * Capture user interactions: clicks (with element info and coordinates)
   * and scroll depth (debounced, tracks max depth per page).
   */
  private observeInteractions() {
    // Click tracking — capture on the capture phase to get all clicks
    this.boundClickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Build a concise CSS selector for the clicked element
      const selector = this.buildSelector(target);
      const text = (target.textContent || '').trim().slice(0, 100);
      const tagName = target.tagName?.toLowerCase() || '';

      this.push({
        event_type: 'interaction',
        interaction: {
          type: 'click',
          selector,
          tag: tagName,
          text: text || undefined,
          x: event.clientX,
          y: event.clientY,
          page_x: event.pageX,
          page_y: event.pageY,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
        },
      });
    };
    document.addEventListener('click', this.boundClickHandler, true);

    // Scroll depth tracking — debounced, emits when user stops scrolling
    this.boundScrollHandler = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const viewportHeight = window.innerHeight;
      const scrollPercent =
        docHeight > viewportHeight
          ? Math.round(((scrollTop + viewportHeight) / docHeight) * 100)
          : 100;

      if (scrollPercent > this.maxScrollDepth) {
        this.maxScrollDepth = scrollPercent;
      }

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.push({
          event_type: 'interaction',
          interaction: {
            type: 'scroll',
            scroll_depth: this.maxScrollDepth,
            scroll_y: Math.round(scrollTop),
            doc_height: Math.round(docHeight),
            viewport_height: viewportHeight,
          },
        });
      }, 1000);
    };
    window.addEventListener('scroll', this.boundScrollHandler);
  }

  /**
   * Build a concise CSS selector for a DOM element.
   * Returns something like "button.btn-primary#submit" or "a.nav-link".
   */
  private buildSelector(el: HTMLElement) {
    const tag = el.tagName?.toLowerCase() || '';
    const id = el.id ? `#${el.id}` : '';
    const classes = Array.from(el.classList || [])
      .slice(0, 3)
      .map((c) => `.${c}`)
      .join('');
    const role = el.getAttribute('role');
    const roleAttr = role ? `[role="${role}"]` : '';
    const ariaLabel = el.getAttribute('aria-label');
    const ariaAttr = ariaLabel ? `[aria-label="${ariaLabel.slice(0, 50)}"]` : '';

    return `${tag}${id}${classes}${roleAttr}${ariaAttr}`.slice(0, 200);
  }

  // -- Session Replay (rrweb) --

  /**
   * Wait until the app has rendered meaningful DOM content.
   * rrweb's FullSnapshot captures the DOM at record() time, so calling it
   * before the framework renders produces an empty snapshot.
   */
  private waitForRenderedDOM(): Promise<void> {
    return new Promise((resolve) => {
      let elapsed = 0;
      const poll = () => {
        // body has real content once the framework renders (not just script/noscript)
        if (document.body.childElementCount > 2 || elapsed >= 5000) {
          resolve();
        } else {
          elapsed += 100;
          setTimeout(poll, 100);
        }
      };
      if (document.readyState === 'complete') {
        poll();
      } else {
        window.addEventListener('load', poll, { once: true });
      }
    });
  }

  private async startSessionRecording() {
    try {
      const { record } = await import('rrweb');

      // Wait for the app to render before capturing the initial FullSnapshot.
      // Ember.js renders asynchronously after DOM load — recording too early
      // captures only the raw index.html template (empty body).
      await this.waitForRenderedDOM();

      this.replayStopFn =
        record({
          emit: (event: unknown) => {
            if (this.replayBuffer.length < TelemetryService.REPLAY_BUFFER_CAP) {
              this.replayBuffer.push(event);
            }
          },
          maskAllInputs: true,
          // Limit full DOM snapshots to every 5 minutes
          checkoutEveryNms: 300_000,
          // Allow opting out regions with class="no-replay"
          blockSelector: '.no-replay',
          sampling: {
            mousemove: 200,
            mouseInteraction: true,
            scroll: 300,
            input: 'last',
          },
        }) ?? null;

      // Flush immediately to capture the initial Meta + FullSnapshot events.
      // rrweb emits these synchronously during record() — if we wait for the
      // interval timer, they can be lost to HMR or navigation.
      this.flushReplayBuffer();

      // Flush replay data on a separate interval (larger chunks)
      this.replayFlushTimer = setInterval(
        () => this.flushReplayBuffer(),
        this.config?.replayFlushInterval ?? 15_000
      );
    } catch {
      // rrweb import failed — skip silently
    }
  }

  private stopSessionRecording() {
    if (this.replayStopFn) {
      this.replayStopFn();
      this.replayStopFn = null;
    }
  }

  private flushReplayBuffer = () => {
    if (!this.config?.replayEndpoint || this.replayBuffer.length === 0) return;

    const events = this.replayBuffer.splice(0);
    const chunkIndex = this.replayChunkIndex++;

    const payload = JSON.stringify({
      session_id: this.sessionId,
      app: this.config.app,
      chunk_index: chunkIndex,
      events,
      timestamp: new Date().toISOString(),
    });

    const headers: Record<string, string> = {};
    if (this.config.token) {
      headers['X-Telemetry-Token'] = this.config.token;
    }

    this.compressAndSend(this.config.replayEndpoint, payload, headers);
  };

  private compressAndSend(url: string, payload: string, extraHeaders: Record<string, string>) {
    // Use CompressionStream (gzip) when available — typically ~80% smaller
    if (typeof CompressionStream !== 'undefined') {
      const blob = new Blob([payload], { type: 'application/json' });
      const compressed = blob.stream().pipeThrough(new CompressionStream('gzip'));
      new Response(compressed)
        .blob()
        .then((gzBlob) => {
          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Content-Encoding': 'gzip',
              ...extraHeaders,
            },
            body: gzBlob,
            credentials: 'omit',
          }).catch(() => {});
        })
        .catch(() => {});
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...extraHeaders },
        body: payload,
        credentials: 'omit',
      }).catch(() => {});
    }
  }

  private safeObserve(type: string, callback: (entries: PerformanceEntryList) => void) {
    try {
      const observer = new PerformanceObserver((list) => {
        callback(list.getEntries());
      });
      observer.observe({ type, buffered: true } as PerformanceObserverInit);
      this.observers.push(observer);
    } catch {
      // Browser doesn't support this entry type — skip silently
    }
  }

  private generateSessionId() {
    // Reuse session ID within tab lifetime to correlate events
    const key = 'tio_telemetry_session';
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(key, id);
    }
    return id;
  }
}
